import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';

import type { ExistingVolumeOptionItem } from '../../components/existingVolumeSelect';
import type { VendorDeviceMeta } from '../../components/vendorDevice';
import { VENDOR_DEVICE_FIELD_KEYS, buildVendorDeviceMetaFormPatch, normalizeVendorDeviceMeta } from '../../components/vendorDevice';
import { parseGiSize } from '../../utils/diskSize';
import { buildImageListQueryFromResource } from '../../utils/imageQuery';
import { SYSTEM_DISK_NAME } from './constants';
import type { ImageVmCardItem, ImageVmImageListKind, SystemDiskImageInfo, SystemDiskRecord } from './types';

export function buildImageVmQuery(
  architecture: string | undefined,
  gpu: Record<string, unknown> | undefined,
  _listKind: ImageVmImageListKind = 'cloudHost',
): Record<string, unknown> {
  const cpu = architecture != null && String(architecture).trim() !== '' ? String(architecture) : undefined;
  return buildImageListQueryFromResource({ cpu, gpu });
}

function parsePositiveDiskSize(value: unknown): number | undefined {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** 是否具备可展示的系统盘内容（忽略删盘后 Form 嵌套字段残留） */
function hasMeaningfulSystemDiskContent(record: SystemDiskRecord, diskSize: number | undefined, pricing?: VendorDeviceMeta): boolean {
  if (record.sourceKind === 'existing') return true;
  if (diskSize != null) return true;
  if (pricing) return true;
  if (String(record.storageClass ?? '').trim()) return true;
  if (record.imageId || record.image?.url || record.image?.os) return true;
  return false;
}

export function normalizeSystemDiskRecord(raw: unknown): SystemDiskRecord | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const record = raw as SystemDiskRecord;
  const pricing = normalizeVendorDeviceMeta(record.pricing);
  const diskSize = parsePositiveDiskSize(record.diskSize);
  // 删盘后 VendorDevicePersistFields 会把 systemDisk 复活成仅含空 pricing 的壳，不能当成有效盘
  if (!hasMeaningfulSystemDiskContent(record, diskSize, pricing)) return undefined;
  return {
    ...record,
    diskName: record.diskName || SYSTEM_DISK_NAME,
    ...(diskSize != null ? { diskSize } : { diskSize: undefined }),
    sourceKind: record.sourceKind === 'existing' ? 'existing' : 'new',
    ...(pricing ? { pricing } : { pricing: undefined }),
  };
}

/** 清空系统盘字段（含 pricing 子字段，避免 Arco 嵌套 FormItem 残留 rootfs 壳） */
export function clearSystemDiskFormField(form: FormInstance, diskPath: string) {
  const pricingPath = `${diskPath}.pricing`;
  const clearedPricing = Object.fromEntries(VENDOR_DEVICE_FIELD_KEYS.map((key) => [`${pricingPath}.${key}`, undefined]));
  form.setFieldsValue({
    [diskPath]: undefined,
    ...buildVendorDeviceMetaFormPatch(pricingPath, undefined),
    ...clearedPricing,
  });
}

/** 选择已有系统盘：镜像已固化在盘中，不走新建选镜像的预置/限制联动 */
export function isExistingSystemDisk(disk: SystemDiskRecord | undefined): boolean {
  return disk?.sourceKind === 'existing';
}

export function diskToCardItem(disk: SystemDiskRecord, storageClassLabelMap?: Record<string, string>): ImageVmCardItem {
  return {
    diskName: disk.diskName,
    diskNickname: disk.diskNickname || disk.diskName,
    diskSize: disk.diskSize,
    storageClass: disk.storageClass,
    storageClassLabel: disk.storageClass ? storageClassLabelMap?.[disk.storageClass] || disk.storageClass : undefined,
    os: disk.image?.os,
    architecture: disk.image?.architecture,
    locked: disk.locked,
  };
}

export function isRestrictedArchitecture(value: unknown): value is string {
  if (value == null) return false;
  const text = String(value).trim();
  return text !== '' && text !== '-';
}

/** 从镜像元数据读取声明的 CPU 架构；通用镜像返回 undefined */
function readImageArchitecture(item: CommonImageItemType): string | undefined {
  const raw = get(item, 'cpuArchitectures[0]') ?? get(item, 'cpuArchitectures');
  const value = Array.isArray(raw) ? raw[0] : raw;
  return isRestrictedArchitecture(value) ? String(value).trim() : undefined;
}

export function mapCommonImageToSystemDiskImage(item: CommonImageItemType, downloadUrl: string | undefined): SystemDiskImageInfo {
  const arch = readImageArchitecture(item);
  return {
    imageId: item.id ?? item.key,
    name: item.name,
    os: item.vendor,
    ...(arch ? { architecture: arch } : {}),
    version: item.version,
    bootloader: get(item, 'options.systemImage.bootloader'),
    url: downloadUrl,
    source: 'HTTP',
  };
}

/** 新建系统盘未选存储类（已有盘跳过） */
export function isNewSystemDiskMissingStorageClass(disk: unknown): boolean {
  const record = normalizeSystemDiskRecord(disk);
  if (!record || isExistingSystemDisk(record)) return false;
  return !String(record.storageClass ?? '').trim();
}

export function buildSystemDiskFromExistingSelection(selected: ExistingVolumeOptionItem): SystemDiskRecord {
  const origin = selected.origin;
  const diskSize = parseGiSize(get(origin, 'spec.pvc.resources.requests.storage'));
  return {
    diskName: get(origin, 'metadata.name', ''),
    diskNickname: get(origin, 'spec.name', ''),
    diskSize,
    storageClass: get(origin, 'spec.pvc.storageClassName'),
    reclaimPolicy: undefined,
    sourceKind: 'existing',
    imageId: get(origin, 'spec.source.imageFactory.uuid'),
    image: {
      architecture: get(origin, 'spec.system.architecture'),
      bootloader: get(origin, 'spec.system.bootloader'),
      os: get(origin, 'spec.system.os'),
      version: get(origin, 'spec.system.version'),
      imageId: get(origin, 'spec.source.imageFactory.uuid'),
    },
  };
}

export function buildSystemDiskFromCreateForm(values: {
  diskSize?: number;
  storageClass?: string;
  reclaimPolicy?: string;
  imageId?: string;
  image?: SystemDiskImageInfo;
  pricing?: VendorDeviceMeta;
}): SystemDiskRecord {
  return {
    diskName: SYSTEM_DISK_NAME,
    diskNickname: SYSTEM_DISK_NAME,
    ...(values.diskSize != null ? { diskSize: values.diskSize } : {}),
    storageClass: values.storageClass,
    reclaimPolicy: values.reclaimPolicy,
    sourceKind: 'new',
    imageId: values.imageId,
    image: values.image,
    pricing: normalizeVendorDeviceMeta(values.pricing),
  };
}

export function mergeEditedSystemDisk(current: SystemDiskRecord | undefined, next: SystemDiskRecord): SystemDiskRecord {
  return {
    ...next,
    diskSize: next.diskSize ?? current?.diskSize,
    sourceKind: current?.sourceKind ?? next.sourceKind,
    locked: current?.locked,
    pricing: next.pricing ?? current?.pricing,
  };
}
